import { inject, Injectable } from '@angular/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { compact, isEmpty } from 'lodash-es';
import { format, sub } from 'date-fns';
import { Store } from '@ngrx/store';
import { BackendService } from '@shared/services';
import { ExceptionHistoryListResponse, ExceptionItem, ExceptionItemRaw, ExceptionListResponse, RequestPageParams } from '@shared/models';
import { constants } from '@shared/constants';
import { exceptionCategory, exceptionType, filterCreatedAtDateRange, systemTimeZone } from '@shared/utils';
import { environment } from '@env';

@Injectable({
  providedIn: 'root',
})
export class ExceptionManagementService {
  store = inject(Store);

  backendService = inject(BackendService);

  getExeptions({
    requestParams,
    assignedTo,
  }: {
    requestParams: RequestPageParams;
    assignedTo: string[];
  }): Observable<{ items: ExceptionItem[]; totalElements: number }> {
    const { page, size, activeFilters, sortParams } = requestParams;

    let params = new HttpParams().set('page', `${page ?? 0}`).set('size', `${size ?? constants.TABLE_ROWS}`);

    if (sortParams) {
      params = params.append('sort', `${sortParams.key},${sortParams.sortDir}`);
    }

    if (assignedTo.length) {
      params = params.append('assignedTo', assignedTo.join(','));
    }

    if (!isEmpty(activeFilters)) {
      Object.keys(activeFilters).forEach((key) => {
        if (key === 'date') {
          const defaultRange = {
            from: format(sub(new Date(), { days: 30 }), 'yyyy-MM-dd'),
            to: format(new Date(), 'yyyy-MM-dd'),
          };

          const { from, to } = filterCreatedAtDateRange(activeFilters[key], defaultRange) ?? {};

          if (!from || !to) {
            return;
          }

          if (key === 'date') {
            params = params.append('createdDateFrom', from);
            params = params.append('createdDateTo', to);
            params = params.append('timeZone', systemTimeZone);
            return;
          }
        } else {
          params = params.append(key, activeFilters[key]!);
        }
      });
    }

    return this.backendService
      .get<ExceptionListResponse>(`${environment.exceptionService}`, {
        params,
      })
      .pipe(
        map((response) => {
          const { content = [], totalElements = 0 } = response;
          return { items: content.map((item) => this.mapExceptionItem(item)), totalElements };
        }),
      );
  }

  getExeptionDetails({ exceptionId }: { exceptionId: string }): Observable<ExceptionItem> {
    return this.backendService
      .get<ExceptionItemRaw>(`${environment.exceptionService}/${exceptionId}`)
      .pipe(map((response) => this.mapExceptionItem(response)));
  }

  updateExceptionById({
    exceptionId,
    data,
  }: {
    exceptionId: string;
    data: { assignedTo?: string | null; status?: string };
  }): Observable<ExceptionItem> {
    const headers = new HttpHeaders().set('Content-Type', 'application/merge-patch+json');

    return this.backendService
      .patch<ExceptionItemRaw>(`${environment.exceptionService}/${exceptionId}`, data, { headers })
      .pipe(map((response) => this.mapExceptionItem(response)));
  }

  getExeptionHistory({
    exceptionId,
    requestParams,
  }: {
    exceptionId: string;
    requestParams: RequestPageParams;
  }): Observable<ExceptionHistoryListResponse> {
    const { page, size, sortParams } = requestParams;

    let params = new HttpParams().set('page', `${page ?? 0}`).set('size', `${size ?? 100}`);

    if (sortParams) {
      params = params.append('sort', `${sortParams.key},${sortParams.sortDir}`);
    }

    return this.backendService.get<ExceptionHistoryListResponse>(`${environment.exceptionService}/${exceptionId}/history`, {
      params,
    });
  }

  mapExceptionItem(item: ExceptionItemRaw): ExceptionItem {
    const assignedToName = item.assignedTo ? compact([item.assignedTo.firstName, item.assignedTo.lastName]).join(' ') : '';
    const assignedToEmail = item.assignedTo?.email ?? ' ';
    return {
      ...item,
      typeLabel: exceptionType[item.type] ?? item.type,
      categoryLabel: exceptionCategory[item.category] ?? item.category,
      assignee: `${assignedToName}::${assignedToEmail}`,
    };
  }
}
